import { WithPuckProps } from '@measured/puck';
import { hasValue } from '@psyomics/ui-common';
import { useMemo } from 'react';

import { useRenderContext } from '../../../context/render-context';
import { AnsweredStatus, useAllSurveyConditions } from '../../../utils/surveys/conditions';
import * as expressionUtils from '../../../utils/surveys/expressions';

import { NotShownConditionContent, ShownContent } from './components/content-sections';
import { ErrorBlock } from './components/error-block';
import { MeasureDetails } from './components/measure-details';
import { useExpression } from './hooks/use-expression';
import { MeasureBreakdownProps } from './types';

const useAllSkippedOrNotShown = (expressionName?: string) => {
  const allConditions = useAllSurveyConditions(expressionName);

  return useMemo(() => {
    const values = Object.values(allConditions).filter(hasValue);
    const allSkippedOrNotShown = values.every(
      (ci) => ci.answeredStatus === AnsweredStatus.SKIPPED || ci.answeredStatus === AnsweredStatus.NOT_SHOWN
    );
    const allNotShown = values.every((ci) => ci.answeredStatus === AnsweredStatus.NOT_SHOWN);
    const allSkipped = values.every((ci) => ci.answeredStatus === AnsweredStatus.SKIPPED);

    return { allSkippedOrNotShown, allNotShown, allSkipped };
  }, [allConditions]);
};

const MeasuresBreakdownBlock = ({
  additionalContent,
  selectedExpressionName,
  description,
  answerBreakdownKey,
  answerBreakdown,
  title,
  allRespondents = false,
  puck,
  hideMainMeasure,
}: WithPuckProps<MeasureBreakdownProps>) => {
  const { survey: refSurvey, answers: refAnswers } = useRenderContext();
  const refExpression = useExpression({ survey: refSurvey, selectedExpressionName });
  const { allSkipped, allNotShown, allSkippedOrNotShown } = useAllSkippedOrNotShown(selectedExpressionName);

  if (!refSurvey) return <div>No survey</div>;
  if (!refAnswers) return <div>No answers</div>;

  if (!description || description.trim() === '') return <div>Please provide a description</div>;

  if (!refExpression) return <ErrorBlock error="no-expression"></ErrorBlock>;
  if (!expressionUtils.isMeasureExpression(refExpression)) return <ErrorBlock error="wrong-expression"></ErrorBlock>;

  return (
    <div>
      <MeasureDetails
        allRespondents={allRespondents}
        allSkipped={allSkipped}
        description={description}
        expression={refExpression}
        hideMainMeasure={hideMainMeasure}
        isPdf={puck.metadata.isPdf}
        title={title}
      />
      {!puck.isEditing && allSkippedOrNotShown ? (
        <NotShownConditionContent isNotShown={allNotShown} isSkipped={allSkipped} />
      ) : (
        <ShownContent
          additionalContent={additionalContent}
          allRespondents={allRespondents}
          answerBreakdown={answerBreakdown}
          answerBreakdownKey={answerBreakdownKey}
          survey={refSurvey}
        />
      )}
    </div>
  );
};

export type { MeasureBreakdownProps };
export { MeasuresBreakdownBlock };
